import {
  AttendanceEmployeesSettingExtraHoursTransferFrequencyEnum,
  AttendanceEnumsBalanceCarryoverResetFrequencyEnum,
  AttendanceEnumsOvertimePoolUpdateFrequencyEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

export const DEFAULT_UPDATE_FREQUENCY: AttendanceEnumsOvertimePoolUpdateFrequencyEnum = 'monthly'

const updateFrequencyTypeMap: Record<string, AttendanceEnumsOvertimePoolUpdateFrequencyEnum> = {
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
  at_cycle_end: 'at_cycle_end',
  on_approval: 'on_approval',
  yearly: 'monthly',
  never: 'monthly',
  custom_period: 'monthly',
}

export const updateFrequencyToEnum = (
  frequency: string
): AttendanceEnumsOvertimePoolUpdateFrequencyEnum => {
  const result = updateFrequencyTypeMap[frequency]
  if (!result) {
    throw new Error(`Unknown frequency: ${frequency}`)
  }
  return result
}

export const getUpdateFrequencyOptions = (): Array<{
  label: string
  value: AttendanceEnumsOvertimePoolUpdateFrequencyEnum
}> => [
  {
    label: i18n.t('time_tracking.settings.balance.reset_frequency.daily'),
    value: 'daily',
  },
  {
    label: i18n.t('time_tracking.settings.balance.reset_frequency.weekly'),
    value: 'weekly',
  },
  {
    label: i18n.t('time_tracking.settings.balance.reset_frequency.monthly'),
    value: 'monthly',
  },
]

type ValidCombination = {
  transferFrequency: AttendanceEmployeesSettingExtraHoursTransferFrequencyEnum
  resetFrequency: AttendanceEnumsBalanceCarryoverResetFrequencyEnum
}

const validCombinations: Record<
  AttendanceEnumsOvertimePoolUpdateFrequencyEnum,
  ValidCombination[]
> = {
  daily: [
    { transferFrequency: 'daily', resetFrequency: 'weekly' },
    { transferFrequency: 'daily', resetFrequency: 'monthly' },
    { transferFrequency: 'daily', resetFrequency: 'yearly' },
    { transferFrequency: 'daily', resetFrequency: 'never' },
  ],
  weekly: [
    { transferFrequency: 'daily', resetFrequency: 'weekly' },
    { transferFrequency: 'weekly', resetFrequency: 'weekly' },
    { transferFrequency: 'daily', resetFrequency: 'never' },
    { transferFrequency: 'daily', resetFrequency: 'yearly' },
    { transferFrequency: 'daily', resetFrequency: 'monthly' },
    { transferFrequency: 'weekly', resetFrequency: 'never' },
    { transferFrequency: 'weekly', resetFrequency: 'yearly' },
    { transferFrequency: 'weekly', resetFrequency: 'monthly' },
  ],
  monthly: [
    { transferFrequency: 'daily', resetFrequency: 'monthly' },
    { transferFrequency: 'monthly', resetFrequency: 'monthly' },
    { transferFrequency: 'daily', resetFrequency: 'yearly' },
    { transferFrequency: 'monthly', resetFrequency: 'yearly' },
    { transferFrequency: 'daily', resetFrequency: 'never' },
    { transferFrequency: 'monthly', resetFrequency: 'never' },
  ],
  at_cycle_end: [],
  on_approval: [],
  yearly: [],
}

export const validateCompensationPeriodCompatibility = (
  updateFrequency: string,
  transferFrequency: string,
  resetFrequency: string
): { isValid: boolean; errorMessage?: string } => {
  const enumFrequency = updateFrequencyToEnum(updateFrequency)
  const validOptions = validCombinations[enumFrequency]
  const isValidCombination = validOptions.some(
    (option) =>
      option.transferFrequency === transferFrequency && option.resetFrequency === resetFrequency
  )

  if (!isValidCombination) {
    return {
      isValid: false,
      errorMessage: i18n.t('time_tracking.settings.compensation.validation_warning'),
    }
  }

  return { isValid: true }
}
